import { useCallback, useEffect, useRef, useState } from 'react'
import { api, type BroadcastDraft, type BroadcastPreview } from '../lib/api'
import { useToast } from '../lib/toast'
import type { BroadcastRecipientRow, BroadcastSummary } from '@shared/types'

/**
 * Broadcasts: an announcement or a survey, to every account holder.
 *
 * The one screen in this app that can email a lot of strangers at once, so it
 * is built to be slow in the right place. The count on the send button is the
 * resolved audience rather than an estimate, the send itself takes two taps,
 * and "Send it to me first" sits between the writing and the sending because
 * the only reliable way to know how an email reads is to read it in an inbox.
 *
 * Every send is kept. The history tab is not a nicety -- "who got that, and
 * did it arrive" is the question you have the morning after, and an answer
 * that depends on remembering is not an answer.
 */

type Tab = 'compose' | 'history'

const BLANK: BroadcastDraft = {
  subject: '',
  markdown: '',
  audience: { statuses: ['active', 'pending'] },
}

const PLACEHOLDER = `Write in markdown.

## A heading

Some words, with **bold**, *italic* and a [link](https://app.fivelunches.com).

- a list
- of things

> a quote`

export function BroadcastAdmin({ onError }: { onError: (err: unknown, fallback: string) => void }) {
  const toast = useToast()
  const [tab, setTab] = useState<Tab>('compose')
  const [draft, setDraft] = useState<BroadcastDraft>(BLANK)
  const [preview, setPreview] = useState<BroadcastPreview | null>(null)
  const [history, setHistory] = useState<BroadcastSummary[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  /** Set by the first tap on Send. The second tap is the one that sends. */
  const [armed, setArmed] = useState(false)
  const [open, setOpen] = useState<string | null>(null)

  const loadHistory = useCallback(() => {
    api
      .broadcasts()
      .then(setHistory)
      .catch((e) => onError(e, 'Could not load past broadcasts.'))
  }, [onError])

  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  /*
   * The preview is asked for from the server, not rendered here, so what is on
   * screen came through exactly the same markdown renderer the email will use.
   * A preview with its own renderer is a preview that can lie.
   */
  const draftKey = JSON.stringify(draft)
  useEffect(() => {
    const timer = setTimeout(() => {
      api
        .previewBroadcast(draft)
        .then(setPreview)
        .catch(() => setPreview(null))
    }, 350)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey])

  // Any edit disarms: the count you confirmed must be the count you saw.
  useEffect(() => setArmed(false), [draftKey])

  const ready = draft.subject.trim() !== '' && draft.markdown.trim() !== ''
  const count = preview?.recipients ?? 0

  const sendTest = async () => {
    setBusy('test')
    try {
      const r = await api.testBroadcast(draft)
      toast.notify(`Sent to ${r.sentTo}.`)
    } catch (e) {
      onError(e, 'Could not send the test.')
    } finally {
      setBusy(null)
    }
  }

  const send = async () => {
    setBusy('send')
    try {
      const r = await api.sendBroadcast(draft)
      toast.notify(`Sending to ${r.recipients}.`)
      setDraft(BLANK)
      setArmed(false)
      setTab('history')
      setOpen(r.id)
      loadHistory()
    } catch (e) {
      onError(e, 'Could not send it.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Broadcast</h1>
          <p>
            An announcement or a survey, to every account holder. Everyone gets an unsubscribe
            link, and it only takes them off these — never their reminder.
          </p>
        </div>
        <div className="row">
          <button
            className={`btn sm ${tab === 'compose' ? 'primary' : ''}`}
            onClick={() => setTab('compose')}
          >
            Compose
          </button>
          <button
            className={`btn sm ${tab === 'history' ? 'primary' : ''}`}
            onClick={() => setTab('history')}
          >
            Sent{history?.length ? ` (${history.length})` : ''}
          </button>
        </div>
      </div>

      {tab === 'compose' ? (
        <div className="bc-grid">
          <div className="bc-compose">
            <div className="field">
              <label htmlFor="bc-subject">Subject</label>
              <input
                id="bc-subject"
                value={draft.subject}
                maxLength={140}
                placeholder="What is this about?"
                onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
              />
            </div>

            <div className="field">
              <label htmlFor="bc-body">Body — markdown</label>
              <textarea
                id="bc-body"
                className="bc-body"
                rows={16}
                value={draft.markdown}
                maxLength={20000}
                placeholder={PLACEHOLDER}
                onChange={(e) => setDraft({ ...draft, markdown: e.target.value })}
              />
              <p className="muted bc-hint">
                Headings, <strong>**bold**</strong>, <em>*italic*</em>, `code`, [links](url),
                lists, &gt; quotes and --- rules. Anything else is plain text.
              </p>
            </div>

            <Audience draft={draft} onChange={setDraft} />
          </div>

          <div className="bc-side">
            <div className="bc-preview-head">
              <strong>Preview</strong>
              <span className="muted">as it will arrive</span>
            </div>
            {/*
              The server's own render, and its renderer escapes everything it is
              given -- raw HTML in the source comes back as visible text rather
              than markup, which is why this can be dropped in as it is.
            */}
            <div
              className="bc-preview"
              dangerouslySetInnerHTML={{ __html: preview?.blocks || '<p><em>Nothing yet.</em></p>' }}
            />

            <div className="bc-actions">
              <p className="bc-count">
                {preview ? (
                  <>
                    <strong>{count}</strong> {count === 1 ? 'person' : 'people'} will get this
                  </>
                ) : (
                  'Counting…'
                )}
              </p>
              {/*
                Addresses, not just first names. Checking a filter means
                checking who it caught, and "Emily, Jennifer" cannot tell you
                whether it caught the right Emily. It scrolls rather than
                pushing the send button off the screen.
              */}
              {preview?.sample?.length ? (
                <>
                  <ul className="bc-sample">
                    {preview.sample.map((s) => (
                      <li key={s.email}>
                        {s.name && <span className="bc-sample-name">{s.name}</span>}
                        <span className="bc-sample-email">{s.email}</span>
                      </li>
                    ))}
                  </ul>
                  {count > preview.sample.length && (
                    <p className="muted bc-hint">
                      and {count - preview.sample.length} more not shown
                    </p>
                  )}
                </>
              ) : null}

              <button className="btn" disabled={!ready || busy !== null} onClick={sendTest}>
                {busy === 'test' ? 'Sending…' : 'Send it to me first'}
              </button>

              {armed ? (
                <>
                  <button
                    className="btn primary"
                    disabled={busy !== null}
                    onClick={send}
                  >
                    {busy === 'send' ? 'Sending…' : `Yes — send to ${count}`}
                  </button>
                  <button className="btn ghost" onClick={() => setArmed(false)}>
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  className="btn danger"
                  disabled={!ready || count === 0 || busy !== null}
                  onClick={() => setArmed(true)}
                >
                  Send to {count}…
                </button>
              )}
              {armed && (
                <p className="muted bc-hint">
                  This cannot be taken back once it goes.
                </p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <History
          history={history}
          open={open}
          onOpen={setOpen}
          onError={onError}
          onSettled={loadHistory}
          onReuse={(b) => {
            setDraft({ subject: b.subject, markdown: b.markdown, audience: b.audience })
            setTab('compose')
          }}
        />
      )}
    </>
  )
}

/* ---- Audience ------------------------------------------------------------ */

function Audience({
  draft,
  onChange,
}: {
  draft: BroadcastDraft
  onChange: (d: BroadcastDraft) => void
}) {
  const statuses = draft.audience.statuses
  const toggle = (s: 'active' | 'pending') => {
    const next = statuses.includes(s) ? statuses.filter((x) => x !== s) : [...statuses, s]
    onChange({ ...draft, audience: { ...draft.audience, statuses: next } })
  }

  return (
    <fieldset className="bc-audience">
      <legend>Who gets it</legend>
      <label className="bc-check">
        <input type="checkbox" checked={statuses.includes('active')} onChange={() => toggle('active')} />
        Approved families
      </label>
      <label className="bc-check">
        <input type="checkbox" checked={statuses.includes('pending')} onChange={() => toggle('pending')} />
        Still waiting for approval
      </label>
      <div className="field">
        <label htmlFor="bc-since">Only families who signed up on or after</label>
        <input
          id="bc-since"
          type="date"
          value={draft.audience.since ?? ''}
          onChange={(e) =>
            onChange({
              ...draft,
              audience: { ...draft.audience, since: e.target.value || undefined },
            })
          }
        />
      </div>
      <p className="muted bc-hint">
        Account holders only — people who signed up themselves, plus partners who accepted an
        invitation. Contacts added just to receive the shopping list are never included, and
        neither is anyone who has unsubscribed.
      </p>
    </fieldset>
  )
}

/* ---- History ------------------------------------------------------------- */

function History({
  history,
  open,
  onOpen,
  onError,
  onReuse,
  onSettled,
}: {
  history: BroadcastSummary[] | null
  open: string | null
  onOpen: (id: string | null) => void
  onError: (err: unknown, fallback: string) => void
  onReuse: (b: BroadcastSummary) => void
  onSettled: () => void
}) {
  if (!history) return <p className="muted">Loading…</p>
  if (history.length === 0) return <p className="muted">Nothing has been broadcast yet.</p>

  return (
    <ul className="bc-list">
      {history.map((b) => (
        <li key={b.id} className="bc-item">
          <div className="bc-item-head">
            <button className="linklike" onClick={() => onOpen(open === b.id ? null : b.id)}>
              {b.subject}
            </button>
            <span className={`bc-tag ${b.status}`}>{b.status}</span>
          </div>
          <p className="muted bc-hint">
            {b.createdAt} · {b.counts.sent} sent
            {b.counts.failed > 0 ? `, ${b.counts.failed} failed` : ''}
            {b.counts.skipped > 0 ? `, ${b.counts.skipped} unsubscribed first` : ''}
            {b.counts.queued > 0 ? `, ${b.counts.queued} still going` : ''}
            {b.createdBy ? ` · by ${b.createdBy}` : ''}
            {' · '}
            <button className="linklike" onClick={() => onReuse(b)}>
              Start a new one from this
            </button>
          </p>
          {open === b.id && <Detail id={b.id} onError={onError} onSettled={onSettled} />}
        </li>
      ))}
    </ul>
  )
}

function Detail({
  id,
  onError,
  onSettled,
}: {
  id: string
  onError: (err: unknown, fallback: string) => void
  onSettled: () => void
}) {
  const [rows, setRows] = useState<BroadcastRecipientRow[] | null>(null)
  const [status, setStatus] = useState<BroadcastSummary['status'] | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  /*
   * The summary line above these rows was loaded when the send began, and the
   * send finishes in the background after that. Without this the header goes
   * on saying "sending, 1 still going" over rows that plainly already failed.
   * Once, on the pass that sees it settle -- reloading the list re-renders
   * this component, and a callback on every poll would never stop.
   */
  const told = useRef(false)

  useEffect(() => {
    let live = true

    const load = () => {
      api
        .broadcast(id)
        .then((r) => {
          if (!live) return
          setRows(r.recipients)
          setStatus(r.broadcast.status)
          // Only poll while there is something left to watch.
          if (r.broadcast.status === 'sending') {
            timer.current = setTimeout(load, 3000)
          } else if (!told.current) {
            told.current = true
            onSettled()
          }
        })
        .catch((e) => live && onError(e, 'Could not load that broadcast.'))
    }

    load()
    return () => {
      live = false
      if (timer.current) clearTimeout(timer.current)
    }
  }, [id, onError, onSettled])

  if (!rows) return <p className="muted bc-hint">Loading…</p>

  return (
    <>
      {status === 'sending' && <p className="muted bc-hint">Still sending — this updates itself.</p>}
      <ul className="bc-recipients">
        {rows.map((r) => (
          <li key={r.email} className={`bc-recipient ${r.status}`}>
            <span className="bc-recipient-who">{r.name || r.email}</span>
            <span className="muted">{r.email}</span>
            <span className={`bc-tag ${r.status}`}>{r.status}</span>
            {r.status === 'failed' && r.detail && <span className="bc-why">{r.detail}</span>}
          </li>
        ))}
      </ul>
    </>
  )
}
